import Text from 'components/Text';
import { cn } from 'utils/classNames';
import { useI18n } from 'utils/i18n';
import { getWeightedPercentage } from 'utils/math';
import { makeSpacesNoBreak } from 'utils/string';

interface Point {
	count: number;
	text: string;
}
interface Props {
	className?: string;
	description?: string;
	points: Point[];
	title: string;
}

/**
 * Takes an array of options and calculates the weighted average of them based
 * on the count in each one. That average is displayed on a line scale.
 */
export default function WeightedAverageScale({
	className,
	description,
	points,
	title,
}: Props) {
	const { t } = useI18n();
	const percentage = getWeightedPercentage(...points.map((p) => p.count));
	const lastPointIndex = points.length - 1;

	return (
		<div className={className}>
			<Text as="h4">{title}</Text>
			{description && <p className="mt-1 text-sm">{description}</p>}
			<div className="mt-6">
				<div className="relative w-full border-t-2 border-t-current">
					<div
						className="absolute -top-2 -mt-px size-4 rounded-full bg-current forced-colors:bg-[CanvasText]"
						style={{ left: `${percentage}%` }}
					/>
				</div>
				<div className="mt-4 flex">
					{points.map((point, index) => (
						<p
							key={point.text}
							className={cn(
								'min-w-0 flex-1 wrap-break-word',
								index === 0 && 'pr-2',
								index > 0 && index < lastPointIndex && 'px-2 text-center',
								index === lastPointIndex && 'pl-2 text-right',
							)}
						>
							{`${point.text} (${makeSpacesNoBreak(t('general_amount_label', { amount: point.count }))})`}
						</p>
					))}
				</div>
			</div>
		</div>
	);
}
WeightedAverageScale.displayName = 'WeightedAverageScale';
